import { authState } from './auth.js';

/**
 * Get the assets API base URL. The literal `process.env.PUBLIC_ASSETS_API_URL`
 * is replaced with a string at bundle time by Bun's `PUBLIC_*` env-inlining
 * (`env: 'PUBLIC_*'` in build.js / dev-server.js) — the `PUBLIC_` prefix is the
 * inlining allowlist (like Vite's old `VITE_`), and the ASSETS scope keeps it
 * distinct from rpgish's URL in the shared `.env.<env>` file. Only literal
 * `process.env.PUBLIC_*` reads are replaced, so do not destructure or alias it.
 * Missing/empty is a configuration error — fail loud so a misbuilt bundle
 * doesn't silently route requests against the SPA's own origin (index.html
 * shows the failure on the page).
 * @returns {string}
 */
export function getBackendUrl() {
    // When the variable isn't set, Bun leaves the read as written, and a
    // browser has no `process`: catch that so the message below is what fails.
    let url;
    try {
        url = process.env.PUBLIC_ASSETS_API_URL;
    } catch {
        url = undefined;
    }
    if (!url) {
        throw new Error(
            'PUBLIC_ASSETS_API_URL is not set. The assets web bundle was built without an API URL ' +
                '— rebuild with PUBLIC_ASSETS_API_URL set to the assets API host (e.g. https://api.assets.dicedriven.com).',
        );
    }
    return url;
}

/**
 * Get full URL for an image path
 * @param {string|null|undefined} url
 * @returns {string|null}
 */
export function getImageUrl(url) {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return getBackendUrl() + url;
}

/**
 * Read a success response's JSON body, tolerating an empty one.
 *
 * A 204/205 — and any zero-length body — is a valid success response, so it must
 * not be treated as a parse failure: `response.json()` on an empty body throws
 * `SyntaxError: Unexpected end of JSON input`, which surfaced to users as the
 * error text when accepting a platform agreement (issue #132). The `!response.ok`
 * branches below already tolerated an empty body; only the success paths didn't.
 *
 * Returns null for "no body" rather than `{}` so a later property read fails
 * loudly instead of silently coming back undefined.
 *
 * @param {Response} response
 * @returns {Promise<any>} the parsed body, or null when there is none
 */
export async function parseJsonBody(response) {
    if (response.status === 204 || response.status === 205) return null;
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

/**
 * Exported so a test can construct its own client. `download.test.js` replaces
 * this module's `api` singleton with `mock.module`, which is process-wide, so a
 * test that imported the singleton would get that mock depending on the order
 * bun happens to discover test files in — which differs between macOS and CI.
 * A locally constructed client is immune to that.
 */
export class ApiClient {
    constructor() {
        this.baseUrl = getBackendUrl();
    }

    async fetch(path, options = {}) {
        const url = `${this.baseUrl}${path}`;
        const headers = new Headers(options.headers || {});

        try {
            const token = await authState.getAccessToken();
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
        } catch {
            // Not authenticated — request will go without token
        }

        if (!headers.has('Content-Type') && options.body && typeof options.body === 'string') {
            headers.set('Content-Type', 'application/json');
        }

        return fetch(url, { ...options, headers });
    }

    async get(path) {
        const response = await this.fetch(path);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
                errorData.message || `API error: ${response.status} ${response.statusText}`,
            );
        }
        return parseJsonBody(response);
    }

    async post(path, data) {
        const options = { method: 'POST' };
        if (data) options.body = JSON.stringify(data);
        const response = await this.fetch(path, options);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
                errorData.message || `API error: ${response.status} ${response.statusText}`,
            );
        }
        return parseJsonBody(response);
    }

    async put(path, data) {
        const options = { method: 'PUT' };
        if (data) options.body = JSON.stringify(data);
        const response = await this.fetch(path, options);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
                errorData.message || `API error: ${response.status} ${response.statusText}`,
            );
        }
        return parseJsonBody(response);
    }

    async patch(path, data) {
        const options = { method: 'PATCH' };
        if (data) options.body = JSON.stringify(data);
        const response = await this.fetch(path, options);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
                errorData.message || `API error: ${response.status} ${response.statusText}`,
            );
        }
        return parseJsonBody(response);
    }

    async delete(path) {
        const response = await this.fetch(path, { method: 'DELETE' });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
                errorData.message || `API error: ${response.status} ${response.statusText}`,
            );
        }
        return parseJsonBody(response);
    }

    async upload(path, formData) {
        const url = `${this.baseUrl}${path}`;
        const headers = new Headers();

        try {
            const token = await authState.getAccessToken();
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
        } catch {
            // Not authenticated
        }

        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
                errorData.message || `Upload error: ${response.status} ${response.statusText}`,
            );
        }

        return parseJsonBody(response);
    }

    getImageUrl(url) {
        return getImageUrl(url);
    }
}

/** @type {ApiClient} */
export const api = new ApiClient();
